import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Loader2, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalInvitePreferencesCard() {
  const [autoSend, setAutoSend] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/portal/invite-preferences`);
        setAutoSend(!!res.data?.auto_send_portal_invites);
      } catch (err) {
        // Settings unavailable for tenantless users — silently no-op
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleToggle = async (checked) => {
    setSaving(true);
    const previous = autoSend;
    setAutoSend(checked);
    try {
      await axios.put(`${API}/portal/invite-preferences`, {
        auto_send_portal_invites: checked,
      });
      toast.success(checked ? "Portal invites will auto-send by default" : "Portal invites set to manual send");
    } catch (err) {
      setAutoSend(previous);
      toast.error(err.response?.data?.detail || "Failed to update preference");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="portal-invite-prefs-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Portal Invitations
        </CardTitle>
        <CardDescription>
          Choose whether portal invitations are emailed automatically when you add a new client or employee.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Label htmlFor="auto-send-toggle" className="font-medium cursor-pointer">
                Auto-send portal invites
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                When ON, the "Email setup link now" checkbox on each new client/employee form is pre-checked.
                You can still toggle it off per-record.
              </p>
            </div>
            <Switch
              id="auto-send-toggle"
              checked={autoSend}
              onCheckedChange={handleToggle}
              disabled={saving}
              data-testid="auto-send-invites-toggle"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
